/* Easy Trail concept port. Every section owns and cleans up its listeners/observers. */
(() => {
  'use strict';
  if (window.nfHomepageLoaded) return;
  window.nfHomepageLoaded = true;
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function mount(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanup = [];
    const on = (node, type, fn, options = {}) => node.addEventListener(type, fn, { ...options, signal });
    root.classList.add('nf-enhanced');
    instances.set(root, () => { controller.abort(); cleanup.forEach(fn => fn()); });

    for (const list of root.querySelectorAll('[data-nf-tabs]')) {
      const tabs = [...list.querySelectorAll('[role="tab"]')];
      const panelFor = tab => document.getElementById(tab.getAttribute('aria-controls'));
      const select = (tab, focus = false) => {
        for (const item of tabs) {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          const panel = panelFor(item);
          if (panel) panel.hidden = !selected;
        }
        if (focus) tab.focus({ preventScroll: true });
        root.dispatchEvent(new Event('nf:tab-change'));
      };
      if (!tabs.length) continue;
      list.hidden = false;
      select(tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
      tabs.forEach((tab, index) => {
        on(tab, 'click', () => select(tab));
        on(tab, 'keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); select(tabs[next], true); }
        });
      });
      on(root, 'shopify:block:select', event => {
        const panel = event.target.closest('[role="tabpanel"]');
        const tab = tabs.find(item => panelFor(item) === panel);
        if (tab) select(tab);
      });
    }

    if (root.matches('[data-nf-roster]')) {
      const prev = root.querySelector('[data-nf-prev]');
      const next = root.querySelector('[data-nf-next]');
      const activeTrack = () => [...root.querySelectorAll('[data-nf-track]')].find(track => !track.closest('[role="tabpanel"]').hidden);
      const update = () => {
        const track = activeTrack();
        if (!track) return;
        prev.disabled = track.scrollLeft <= 6;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 6;
        prev.setAttribute('aria-controls', track.parentElement.id);
        next.setAttribute('aria-controls', track.parentElement.id);
      };
      [prev, next].forEach((button, index) => on(button, 'click', () => {
        const track = activeTrack();
        track?.scrollBy({ left: track.clientWidth * (index ? .84 : -.84), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      }));
      for (const track of root.querySelectorAll('[data-nf-track]')) on(track, 'scroll', update, { passive: true });
      on(root, 'nf:tab-change', update);
      const observer = new ResizeObserver(update);
      observer.observe(root);
      cleanup.push(() => observer.disconnect());
      update();
    }

    if (root.matches('[data-nf-header]')) {
      const icon = root.parentElement.querySelector('[data-nf-favicon]');
      const headIcon = document.getElementById('nf-favicon');
      if (icon && headIcon) {
        headIcon.href = icon.href;
        if (icon.type) headIcon.type = icon.type;
        else headIcon.removeAttribute('type');
      }
      const details = root.querySelector('details');
      on(root, 'keydown', event => {
        if (event.key === 'Escape' && details.open) { details.open = false; details.querySelector('summary').focus(); }
      });
      on(details, 'click', event => { if (event.target.closest('a')) details.open = false; });
      let request = 0;
      let timer;
      const refresh = async () => {
        const version = ++request;
        try {
          const response = await fetch(root.dataset.cartUrl, { signal, headers: { Accept: 'application/json' } });
          if (!response.ok) return;
          const cart = await response.json();
          if (version !== request || signal.aborted || !Number.isInteger(cart.item_count)) return;
          const count = root.querySelector('[data-nf-count]');
          count.textContent = String(cart.item_count);
          count.hidden = cart.item_count === 0;
          root.querySelector('[data-nf-cart]').setAttribute('aria-label', root.dataset.cartLabel.replace('[count]', String(cart.item_count)));
        } catch { /* Keep the last server-rendered count if the request fails. */ }
      };
      on(document, 'shopify:cart:lines-update', event => {
        Promise.resolve(event.detail?.promise || event.promise).catch(() => {}).then(() => {
          if (signal.aborted) return;
          refresh();
          clearTimeout(timer);
          timer = setTimeout(refresh, 1500);
        });
      });
      on(window, 'pageshow', event => { if (event.persisted) refresh(); });
      cleanup.push(() => clearTimeout(timer));
    }

    for (const figure of root.querySelectorAll('[data-nf-video]')) {
      const video = figure.querySelector('video');
      const toggle = figure.querySelector('button');
      const note = figure.querySelector('.nf-clip-motion-note');
      let visible = false;
      let manuallyPaused = false;
      const paint = () => {
        figure.classList.toggle('nf-is-playing', !video.paused && !reducedMotion.matches);
        toggle.textContent = video.paused ? figure.dataset.playLabel : figure.dataset.pauseLabel;
        toggle.setAttribute('aria-pressed', String(!video.paused));
      };
      const reconcile = () => {
        toggle.hidden = reducedMotion.matches;
        note.hidden = !reducedMotion.matches;
        if (!visible || reducedMotion.matches || manuallyPaused || document.hidden) { video.pause(); paint(); return; }
        if (!video.getAttribute('src') && video.dataset.nfSrc) video.src = video.dataset.nfSrc;
        if (video.getAttribute('src')) video.play().catch(paint);
      };
      on(toggle, 'click', () => { manuallyPaused = !video.paused; reconcile(); });
      on(video, 'play', paint);
      on(video, 'pause', paint);
      on(video, 'error', () => { figure.classList.remove('nf-is-playing'); toggle.hidden = true; });
      on(reducedMotion, 'change', reconcile);
      on(document, 'visibilitychange', reconcile);
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; reconcile(); }, { threshold: .15 });
      observer.observe(figure);
      cleanup.push(() => { observer.disconnect(); video.pause(); video.removeAttribute('src'); video.load(); });
      reconcile();
    }
  }
  const scan = node => {
    if (node.matches?.('[data-nf-section]')) mount(node);
    node.querySelectorAll?.('[data-nf-section]').forEach(mount);
  };
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of instances) if (event.target === root || event.target.contains(root)) { dispose(); instances.delete(root); }
  });
})();
